/**
 * DLP decisions as their own vocabulary (design evaluation D1, Phase 2).
 *
 * A decision is what a policy did to a piece of data: block it, allow it,
 * caution the user, record that they went ahead, raise an alert, or only log
 * it. src/lib/tone.ts reads these words as severities and states, which drew
 * "block" exactly like a HIGH severity label, made "caution" a calm teal,
 * gave "acknowledged" the same tone as "allow", and coloured "blocked" teal
 * while "block" was orange.
 *
 * The words are normalised here rather than in tone.ts, because tone.ts is
 * shared with d2d-XDR and four other products, where "blocked" is
 * DetectHub's held scan target and "acknowledged" is an alert state. Only a
 * call site that renders a decision (OutcomeBadge `decision`) reads this map,
 * so those meanings stay intact everywhere else.
 *
 * Colour, glyph and shape are decided in ds-dlp.css and OutcomeBadge from
 * the Decision name, so a new API spelling is taught here and nowhere else.
 */

export type Decision = 'block' | 'allow' | 'caution' | 'acknowledged' | 'alert' | 'log';

const DECISION_ALIASES: Record<string, Decision> = {
  block: 'block', blocked: 'block', 'default-block': 'block', default_block: 'block',
  allow: 'allow', allowed: 'allow', 'default-allow': 'allow', default_allow: 'allow',
  caution: 'caution', warn: 'caution', warned: 'caution',
  acknowledged: 'acknowledged', ack: 'acknowledged',
  alert: 'alert',
  log: 'log', logged: 'log',
};

/** The decision a backend word names, or null when it is not a decision. */
export function toDecision(value: string | null | undefined): Decision | null {
  return DECISION_ALIASES[(value ?? '').trim().toLowerCase()] ?? null;
}

/** Every decision, in the order a legend lists them. */
export const DECISIONS: readonly Decision[] = ['block', 'caution', 'acknowledged', 'allow', 'alert', 'log'];
